import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../assests/css/layer-navigation.css";

const COLOR_MAP = {
  white: "#ffffff",
  black: "#111111",
  red: "#d71a39",
  blue: "#2563eb",
  navy: "#1e3a8a",
  green: "#16a34a",
  teal: "#0a8d8c",
  yellow: "#ffbc15",
  grey: "#9ca3af",
  gray: "#9ca3af",
  orange: "#f97316",
  pink: "#ec4899",
  coral: "#fb7185",
  purple: "#7c3aed",
  magenta: "#c026d3",
  brown: "#92400e",
  gold: "#d4af37",
  silver: "#c0c0c0",
};

function decodeHTML(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html || "";
  return txt.value;
}

function getProductPrice(product) {
  return Number(product.price || product.regular_price || 0);
}

function getAttributeOptions(attribute) {
  if (Array.isArray(attribute.options)) {
    return attribute.options.filter(Boolean).map(String);
  }

  return [attribute.option || attribute.value].filter(Boolean).map(String);
}

function isColorAttribute(name = "") {
  return /colou?r/i.test(name);
}

function isSizeAttribute(name = "") {
  return /size|packaging|pack/i.test(name);
}

function FilterSection({ title, onReset, showReset, children }) {
  if (!children) return null;

  return (
    <div className="layer-nav-section">
      <div className="layer-nav-section-head">
        <h4>{title}</h4>
        {showReset ? (
          <button type="button" className="layer-nav-reset" onClick={onReset}>
            Reset
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function LayerNavigation({
  categories = [],
  activeCat,
  products = [],
  filters,
  onChangeFilters,
  isOpen = false,
  onClose,
}) {
  const [categorySearch, setCategorySearch] = useState("");
  const navigate = useNavigate();
  const [priceDraft, setPriceDraft] = useState({
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
  });

  useEffect(() => {
    setPriceDraft({
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
    });
  }, [filters.minPrice, filters.maxPrice]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape" && onClose) {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const parentCategories = useMemo(
    () =>
      categories
        .filter((cat) => cat.parent === 0)
        .filter((cat) =>
          decodeHTML(cat.name).toLowerCase().includes(categorySearch.toLowerCase())
        ),
    [categories, categorySearch]
  );

  const sellers = useMemo(() => {
    const map = new Map();

    products.forEach((product) => {
      const id = product.seller?.id;
      const name = product.seller?.shop_name || product.seller?.name;
      if (!id || !name) return;

      const current = map.get(id) || { id, name, count: 0 };
      current.count += 1;
      map.set(id, current);
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const priceBounds = useMemo(() => {
    const prices = products.map(getProductPrice).filter((price) => price > 0);
    if (prices.length === 0) return null;

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }, [products]);

  const attributeFacets = useMemo(() => {
    const facets = {};

    products.forEach((product) => {
      (product.attributes || []).forEach((attribute) => {
        if (!attribute || attribute.visible === false || !attribute.name) return;

        const name = decodeHTML(attribute.name);
        if (!facets[name]) facets[name] = new Map();

        getAttributeOptions(attribute).forEach((option) => {
          const value = decodeHTML(option);
          facets[name].set(value, (facets[name].get(value) || 0) + 1);
        });
      });
    });

    return Object.entries(facets)
      .map(([name, values]) => ({
        name,
        values: Array.from(values.entries())
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => a.value.localeCompare(b.value)),
      }))
      .filter((facet) => facet.values.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const colorFacet = attributeFacets.find((facet) => isColorAttribute(facet.name));
  const otherFacets = attributeFacets.filter((facet) => !isColorAttribute(facet.name));

  const hasActiveFilters =
    filters.sellers.length > 0 ||
    filters.minPrice !== "" ||
    filters.maxPrice !== "" ||
    filters.rating ||
    Object.values(filters.attributes).some((values) => values.length > 0);

  const emptyFilters = {
    sellers: [],
    minPrice: "",
    maxPrice: "",
    rating: null,
    attributes: {},
  };

  const updateFilters = (next) => onChangeFilters({ ...filters, ...next });

  const toggleSeller = (sellerId) => {
    const sellersNext = filters.sellers.includes(sellerId)
      ? filters.sellers.filter((id) => id !== sellerId)
      : [...filters.sellers, sellerId];

    updateFilters({ sellers: sellersNext });
  };

  const toggleAttribute = (name, value) => {
    const current = filters.attributes[name] || [];
    const nextValues = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];

    updateFilters({
      attributes: {
        ...filters.attributes,
        [name]: nextValues,
      },
    });
  };

  const applyPrice = (nextPrice) => {
    const next = nextPrice || priceDraft;
    updateFilters({
      minPrice: next.minPrice,
      maxPrice: next.maxPrice,
    });
  };

  const updatePriceDraft = (key, value, bound) => {
    const next = { ...priceDraft, [key]: value };

    if (key === "minPrice" && Number(value) > Number(next.maxPrice || bound.max)) {
      next.minPrice = next.maxPrice || bound.max;
    }
    if (key === "maxPrice" && Number(value) < Number(next.minPrice || bound.min)) {
      next.maxPrice = next.minPrice || bound.min;
    }

    setPriceDraft(next);
    applyPrice(next);
  };

  const clearFilters = () => {
    setPriceDraft({ minPrice: "", maxPrice: "" });
    setCategorySearch("");
    onChangeFilters(emptyFilters);
  };

  const minBound = priceBounds?.min || 0;
  const maxBound = priceBounds?.max || 0;
  const minVal = Number(priceDraft.minPrice === "" ? minBound : priceDraft.minPrice);
  const maxVal = Number(priceDraft.maxPrice === "" ? maxBound : priceDraft.maxPrice);
  const range = Math.max(maxBound - minBound, 1);
  const leftPercent = ((minVal - minBound) / range) * 100;
  const rightPercent = ((maxVal - minBound) / range) * 100;

  return (
    <aside className={`shop-sidebar layer-navigation${isOpen ? " is-open" : ""}`}>
      <div className="layer-nav-overlay" onClick={onClose} />
      <div className="layer-nav-box">
        <div className="layer-nav-header">
          <h3>Filters</h3>
          <div className="layer-nav-header-actions">
            {hasActiveFilters ? (
              <button type="button" className="layer-nav-clear" onClick={clearFilters}>
                Clear All
              </button>
            ) : null}
            <button
              type="button"
              className="layer-nav-close"
              onClick={onClose}
              aria-label="Close filters"
            >
              Close <span>×</span>
            </button>
          </div>
        </div>

        <div className="layer-nav-content">

        <FilterSection
          title="Category"
          showReset={Boolean(activeCat)}
          onReset={() => {
            setCategorySearch("");
            navigate("/shop");
          }}
        >
          <div className="layer-nav-search">
            <input
              type="search"
              placeholder="Search"
              value={categorySearch}
              onChange={(event) => setCategorySearch(event.target.value)}
            />
          </div>
          <ul className="layer-nav-list">
            <li>
              <Link className={!activeCat ? "active" : ""} to="/shop">
                All Products
              </Link>
            </li>
            {parentCategories.map((parent) => (
              <li key={parent.id}>
                <Link
                  className={Number(activeCat) === parent.id ? "active" : ""}
                  to={`/category/${parent.id}`}
                >
                  {decodeHTML(parent.name)}
                  <span>({parent.count || 0})</span>
                </Link>
              </li>
            ))}
          </ul>
        </FilterSection>

        {priceBounds && (
          <FilterSection
            title="Price Range"
            showReset={filters.minPrice !== "" || filters.maxPrice !== ""}
            onReset={() => {
              setPriceDraft({ minPrice: "", maxPrice: "" });
              updateFilters({ minPrice: "", maxPrice: "" });
            }}
          >
            <div className="layer-nav-slider">
              <div className="layer-nav-slider-track">
                <div
                  className="layer-nav-slider-range"
                  style={{
                    left: `${leftPercent}%`,
                    width: `${Math.max(rightPercent - leftPercent, 0)}%`,
                  }}
                />
              </div>
              <input
                type="range"
                min={minBound}
                max={maxBound}
                value={minVal}
                onChange={(event) =>
                  updatePriceDraft("minPrice", event.target.value, priceBounds)
                }
              />
              <input
                type="range"
                min={minBound}
                max={maxBound}
                value={maxVal}
                onChange={(event) =>
                  updatePriceDraft("maxPrice", event.target.value, priceBounds)
                }
              />
            </div>

            <div className="layer-nav-price">
              <label>
                <span>₹</span>
                <input
                  type="number"
                  min={minBound}
                  max={maxBound}
                  value={priceDraft.minPrice}
                  placeholder={String(minBound)}
                  onChange={(event) =>
                    setPriceDraft((current) => ({
                      ...current,
                      minPrice: event.target.value,
                    }))
                  }
                  onBlur={() => applyPrice()}
                />
              </label>
              <em>To</em>
              <label>
                <span>₹</span>
                <input
                  type="number"
                  min={minBound}
                  max={maxBound}
                  value={priceDraft.maxPrice}
                  placeholder={String(maxBound)}
                  onChange={(event) =>
                    setPriceDraft((current) => ({
                      ...current,
                      maxPrice: event.target.value,
                    }))
                  }
                  onBlur={() => applyPrice()}
                />
              </label>
            </div>
          </FilterSection>
        )}

        <FilterSection
          title="Rating"
          showReset={Boolean(filters.rating)}
          onReset={() => updateFilters({ rating: null })}
        >
          <div className="layer-nav-rating">
            {[5, 4, 3, 2, 1].map((rating) => (
              <button
                key={rating}
                type="button"
                className={`layer-nav-chip${filters.rating === rating ? " active" : ""}`}
                onClick={() =>
                  updateFilters({ rating: filters.rating === rating ? null : rating })
                }
              >
                <strong>{rating}</strong>
                <span className="layer-nav-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className={star <= rating ? "filled" : ""}>
                      ★
                    </i>
                  ))}
                </span>
              </button>
            ))}
          </div>
        </FilterSection>

        {colorFacet && (
          <FilterSection
            title="Color"
            showReset={(filters.attributes[colorFacet.name] || []).length > 0}
            onReset={() =>
              updateFilters({
                attributes: { ...filters.attributes, [colorFacet.name]: [] },
              })
            }
          >
            <div className="layer-nav-colors">
              {colorFacet.values.map((item) => {
                const swatch = COLOR_MAP[item.value.toLowerCase()] || "#d1d5db";
                const selected = (filters.attributes[colorFacet.name] || []).includes(
                  item.value
                );

                return (
                  <button
                    key={item.value}
                    type="button"
                    title={item.value}
                    className={`layer-nav-swatch${selected ? " active" : ""}`}
                    style={{ background: swatch }}
                    onClick={() => toggleAttribute(colorFacet.name, item.value)}
                  />
                );
              })}
            </div>
          </FilterSection>
        )}

        {sellers.length > 0 && (
          <FilterSection
            title="Seller"
            showReset={filters.sellers.length > 0}
            onReset={() => updateFilters({ sellers: [] })}
          >
            <ul className="layer-nav-check-list">
              {sellers.map((seller) => (
                <li key={seller.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={filters.sellers.includes(seller.id)}
                      onChange={() => toggleSeller(seller.id)}
                    />
                    <span>{seller.name}</span>
                    <em>({seller.count})</em>
                  </label>
                </li>
              ))}
            </ul>
          </FilterSection>
        )}

        {otherFacets.map((facet) => {
          const selected = filters.attributes[facet.name] || [];
          const usePills =
            isSizeAttribute(facet.name) ||
            facet.values.every((item) => item.value.length <= 8);

          return (
            <FilterSection
              key={facet.name}
              title={facet.name}
              showReset={selected.length > 0}
              onReset={() =>
                updateFilters({
                  attributes: { ...filters.attributes, [facet.name]: [] },
                })
              }
            >
              {usePills ? (
                <div className="layer-nav-pills">
                  {facet.values.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={`layer-nav-pill${
                        selected.includes(item.value) ? " active" : ""
                      }`}
                      onClick={() => toggleAttribute(facet.name, item.value)}
                    >
                      {item.value}
                    </button>
                  ))}
                </div>
              ) : (
                <ul className="layer-nav-check-list">
                  {facet.values.map((item) => (
                    <li key={item.value}>
                      <label>
                        <input
                          type="checkbox"
                          checked={selected.includes(item.value)}
                          onChange={() => toggleAttribute(facet.name, item.value)}
                        />
                        <span>{item.value}</span>
                        <em>({item.count})</em>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </FilterSection>
          );
        })}
        </div>

        <div className="layer-nav-footer">
          <button type="button" className="layer-nav-apply" onClick={onClose}>
            Apply Filters
          </button>
          <button type="button" className="layer-nav-clear-link" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>
    </aside>
  );
}

export default LayerNavigation;
